import { type PropsWithChildren, useMemo } from 'react';
import { useLoaderData, useLocation } from 'react-router';
import { CxFooter, HeaderV2 } from '@cx-web/components/composites';
import Logo from '@cx-web/components/primitives/Logo';
import { type FooterV2Response } from '@cx-web/domain-types';
import { logout } from '@cx-web/feature-auth';
import { useOptimisticLoginState } from '@cx-web/feature-auth-ui/provider';
import MiniBetslipWithContextAndStoreSlices from '@cx-web/feature-betslip/components/components/mini-betslip';
import { CookieService } from '@cx-web/feature-cookies';
import { type AdUnitConfiguration } from '@cx-web/feature-gam-advertising';
import { getDefaultState, getStateName, isValidState, type StateCode } from '@cx-web/feature-geolocation';
import { useGeolocationContext } from '@cx-web/feature-geolocation/context';
import { useLabelActions } from '@cx-web/feature-i18n';
import { useMyOCAuth } from '@cx-web/feature-myoc';
import { changeOddsFormat } from '@cx-web/feature-odds-format';
import { useRepubContext } from '@cx-web/feature-repub';
import { useSiteConfig } from '@cx-web/feature-site-config';
import {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS,
  TRACKING_LABELS,
  trackingActions,
} from '@cx-web/feature-tracking';
import { useNavData } from '@cx-web/ui-shell/nav-data-context';
import { useUIConfig } from '@cx-web/ui-shell/ui-config-context';
import { getRepubHomeUrl, getRepubMetaFromRepub } from '@cx-web/utils';
import { useCmpProps } from '@cx-web/utils/cmp';
import { trackEvent } from '@cx-web/utils/dataLayer';

import GoogleAdUnit from '~/components/composites/ads/GoogleAdUnit';
import MiniMyOC from '~/components/composites/MiniMyOC';
import { ONE_DAY } from '~/constants';
import { useCurrentPath } from '~/hooks/use-current-path';
import MobileBottomNav from '~/routes/$/components/MobileBottomNav';
import { type OddsFormat } from '~/types';

export const LayoutComponents = ({
  adUnitConfiguration,
  children,
  footerData,
  userOddsFormat,
}: PropsWithChildren<{
  adUnitConfiguration: AdUnitConfiguration[];
  footerData?: FooterV2Response;
  userOddsFormat: OddsFormat;
}>) => {
  const { myocData } = useMyOCAuth();
  const currentPath = useCurrentPath();
  const { state } = useGeolocationContext();
  const labelActions = useLabelActions();
  const { repub } = useRepubContext();
  const siteConfig = useSiteConfig();
  const loaderData = useLoaderData<{ url: string }>();
  const { pathname } = useLocation();
  const { isAdmiralCookieBannerEnabled, resourcesUrl } = useUIConfig();
  const navData = useNavData();
  const cmpProps = useCmpProps();

  const cookieService = useMemo(() => new CookieService(), []);

  const stateOptions = useMemo(() => {
    const flattenedCodes = Object.values(siteConfig?.supportedStates ?? {}).flat();
    return flattenedCodes
      .map(code => ({ code, label: getStateName(code as StateCode) ?? code }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [siteConfig?.supportedStates]);

  const selectedState = useMemo(() => {
    const stateCode = state as StateCode;
    const flattenedCodes = Object.values(siteConfig?.supportedStates ?? {}).flat();
    if (isValidState(stateCode) && flattenedCodes.includes(stateCode)) return stateCode;
    return siteConfig?.statesAssignments?.[stateCode] ?? getDefaultState(stateCode) ?? '';
  }, [state, siteConfig?.supportedStates, siteConfig?.statesAssignments]);

  // Optimistic login state: show the logged-in header immediately after the myoc widget reports
  // success, without waiting for a page reload to refresh myocData from the server. Driven by the
  // framework-agnostic auth-modal store (NGF-6331) instead of listening to OC:* window events: the
  // legacy bridge translates the OC widget's events into store writes. Shared with atlas-astro's
  // CxHeaderWrapper via useOptimisticLoginState so the two headers can't drift.
  const isOCLoggedIn = useOptimisticLoginState(!!myocData?.myOCUser);

  return (
    <>
      <GoogleAdUnit adUnitConfiguration={adUnitConfiguration} />

      <HeaderV2
        betslipSlot={
          <MiniBetslipWithContextAndStoreSlices betslipTitle={labelActions?.get('RESPONSIVE_BETSLIP') ?? ''} />
        }
        navData={navData}
        navigationKey={pathname}
        oddsFormat={userOddsFormat}
        onMenuToggle={() => {
          trackingActions.eventTracking({
            event: TRACKING_EVENTS.HAMBURGER_MENU_CLICK,
            eventCategory: TRACKING_CATEGORIES.NAVIGATION,
            eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.HAMBURGER_MENU_CLICK],
            eventLabel: TRACKING_LABELS[TRACKING_EVENTS.HAMBURGER_MENU_CLICK],
          });
        }}
        onNavItemClick={({ label }) => {
          trackingActions.eventTracking({
            event: TRACKING_EVENTS.HAMBURGER_MENU_ITEM,
            eventCategory: TRACKING_CATEGORIES.NAVIGATION,
            eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.HAMBURGER_MENU_ITEM],
            eventLabel: label,
          });
        }}
        onOddsFormatChange={changeOddsFormat}
        onStateChange={newState => {
          cookieService.select('Subdivision-Code').set(newState, new Date().getTime() + ONE_DAY);
          cookieService.select('us_state').set(newState, new Date().getTime() + ONE_DAY);
          window.location.reload();
        }}
        renderAccountMenu={variant => (
          <MiniMyOC
            currentPath={currentPath}
            isLoggedIn={isOCLoggedIn}
            labelActions={labelActions}
            myocData={myocData}
            onLogout={() => {
              trackEvent(TRACKING_EVENTS.LOG_OUT, {
                eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
                eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.LOG_OUT],
              });
              logout(repub!, getRepubHomeUrl(repub!));
            }}
            showProfileLink={repub === 'US'}
            siteConfig={siteConfig}
            type={variant === 'desktop' ? 'desktop' : 'burgerTop'}
          />
        )}
        renderLogo={<Logo resourcesUrl={resourcesUrl} />}
        repubHomeUrl={getRepubHomeUrl(repub!)}
        selectedState={selectedState}
        serverUrl={loaderData.url}
        siteConfig={siteConfig}
        stateOptions={stateOptions}
      />
      {children}
      <CxFooter
        cmpProps={cmpProps}
        footerData={footerData}
        isAdmiralCookieBannerEnabled={isAdmiralCookieBannerEnabled}
        resourcesUrl={resourcesUrl}
      />
      <MobileBottomNav isIT={getRepubMetaFromRepub(repub!).repubIs.IT} pathname={pathname} serverUrl={loaderData.url} />
    </>
  );
};
